import { isEqual } from "lodash";
import {
  Product,
  ProductDimensionComponent,
  ProductDimensions,
} from "../../common/models/product";
import { sortBy } from "lodash";
import { calculateMaterialVolume } from "./material-volume-helpers";
import { formatNumber } from "../../common/functions";

export const normalizeDimensionToMm = ({
  unit,
  value,
}: {
  unit: string;
  value: number;
}) => {
  const conversionFactors = {
    mm: 1,
    cm: 10,
    m: 1000,
    ft: 304.8,
    in: 25.4,
    µm: 0.001,
    um: 0.001,
    micron: 0.001,
    microns: 0.001,
  };

  // heuristic: most of the dimensions of our current clients / scrape targets
  // are given in inches so we default to that.
  const factor =
    (conversionFactors as { [key: string]: number | undefined })[unit] ||
    conversionFactors.in;

  return value * factor;
};

/**
 * Micro-scale dimension units (powder particle sizes). They are stored
 * alongside regular dimensions but must never be converted to the buyer's
 * in/mm preference: "15 µm" as "0.00 in" reads as missing data, and before µm
 * was a known unit it was silently treated as inches ("15 in").
 */
export const isMicroUnit = (unit: string | undefined): boolean =>
  ["µm", "um", "micron", "microns"].includes((unit || "").toLowerCase());

export const convertDimensionUnits = (
  fromUnit: string,
  toUnit: string,
  value: number,
): number => {
  return (
    normalizeDimensionToMm({ value, unit: fromUnit }) *
    (1 / normalizeDimensionToMm({ value: 1, unit: toUnit }))
  );
};

const normalizeWeightToKg = ({
  unit,
  value,
}: {
  unit: string;
  value: number;
}) => {
  const conversionFactors = {
    kg: 1,
    g: 0.001,
    lb: 0.453592,
    oz: 0.0283495,
  };
  const factor =
    (conversionFactors as { [key: string]: number | undefined })[unit] ||
    conversionFactors.lb; // default to lb if unit is not recognized
  return value * factor;
};

export const convertWeightUnits = (
  fromUnit: string,
  toUnit: string,
  value: number,
): number => {
  return (
    normalizeWeightToKg({ value, unit: fromUnit }) *
    (1 / normalizeWeightToKg({ value: 1, unit: toUnit }))
  );
};

/**
 * Coerce a stored dimension entry into a {lo, hi} range. Most items store a
 * range, but some scraped feeds (e.g. High Temp Metals) store a single
 * `{value, unit}` leaf. Returns null when nothing usable is present so callers
 * skip the entry instead of dereferencing `undefined.unit` and crashing.
 */
/**
 * A dimension component whose `value` is guaranteed numeric, or null when the
 * record holds something we cannot format. Ingests have stored strings here
 * ("15-45" for a powder's particle band); one such row used to throw
 * `toFixed is not a function` and blank the whole results page.
 */
const numericComponent = (
  c: any,
): ProductDimensionComponent | null => {
  if (!c || typeof c !== "object") return null;
  if (typeof c.value === "number" && Number.isFinite(c.value)) return c;
  const parsed = parseFloat(String(c.value ?? "").replace(",", "."));
  if (!Number.isFinite(parsed)) return null;
  return { ...c, value: parsed };
};

/** "15-45" / "15 - 45" / "15 to 45" stored as one value → [15, 45]. */
const splitStringRange = (value: unknown): [number, number] | null => {
  if (typeof value !== "string") return null;
  const m = value
    .trim()
    .match(/^(\d+(?:[.,]\d+)?)\s*(?:-|–|to)\s*(\d+(?:[.,]\d+)?)$/i);
  if (!m) return null;
  const lo = parseFloat(m[1].replace(",", "."));
  const hi = parseFloat(m[2].replace(",", "."));
  return Number.isFinite(lo) && Number.isFinite(hi) ? [lo, hi] : null;
};

const asDimensionRange = (
  d: any,
): { lo: ProductDimensionComponent; hi: ProductDimensionComponent } | null => {
  if (!d || typeof d !== "object") return null;
  const rawLo = d.lo || d.hi || (d.value !== undefined ? d : null);
  const rawHi = d.hi || d.lo || (d.value !== undefined ? d : null);
  if (!rawLo || !rawHi) return null;

  // A band written as one string on both ends ("15-45") is a real range.
  const band = splitStringRange(rawLo.value);
  if (band && (rawHi.value === rawLo.value || rawHi === rawLo)) {
    return {
      lo: { ...rawLo, value: band[0] },
      hi: { ...rawHi, value: band[1] },
    };
  }

  const lo = numericComponent(rawLo);
  const hi = numericComponent(rawHi);
  if (!lo && !hi) return null;
  return { lo: lo || hi!, hi: hi || lo! };
};

const convertDimensionComponent = (
  dc: ProductDimensionComponent,
  unit: string,
) => {
  const result: ProductDimensionComponent = {
    value: convertDimensionUnits(dc?.unit || "ft", unit, dc?.value ?? 0),
    unit,
  };
  if (dc.tolerance && typeof dc.tolerance.value === "number") {
    result.tolerance = {
      value: convertDimensionUnits(
        dc.tolerance.unit || "ft",
        unit,
        dc.tolerance.value,
      ),
      unit,
    };
  }

  return result;
};

const orderedDimensionKeys = (dimensions: ProductDimensions) => {
  const dimsAscending = [
    "wall_thickness",
    "thickness",
    "inner_diameter",
    "diameter",
    "outer_diameter",
    "inner_height",
    "height",
    "outer_height",
    "inner_width",
    "width",
    "outer_width",
    "length",
    "min_particle_size",
    "max_particle_size",
  ];
  return Object.keys(dimensions).sort((a, b) => {
    const indexA = dimsAscending.indexOf(a);
    const indexB = dimsAscending.indexOf(b);
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
};

export const formatDimensions = (product: Product, unit: string) => {
  if (!product.dimensions) return "-";

  let hasConvertedParts = false;
  const parts = orderedDimensionKeys(product.dimensions)
    .map((label) => {
      const range = asDimensionRange(product.dimensions![label]);
      if (!range) return null;

      // Particle sizes stay in their native micro unit, labeled inline.
      if (isMicroUnit(range.lo?.unit)) {
        const fmt = (value: number) => trimNumber(value.toFixed(2));
        return range.lo.value === range.hi.value
          ? `${fmt(range.lo.value)} ${range.lo.unit}`
          : `${fmt(range.lo.value)}-${fmt(range.hi.value)} ${range.lo.unit}`;
      }

      hasConvertedParts = true;
      const lo = convertDimensionComponent(range.lo, unit);
      const hi = convertDimensionComponent(range.hi, unit);
      const numDecimals = unit === "mm" ? 0 : 2;
      const numToleranceDecimals = 2;

      if (isEqual(lo, hi)) {
        return `${lo.value.toFixed(numDecimals)} ${lo.tolerance ? "±" : ""} ${
          lo.tolerance ? lo.tolerance.value.toFixed(numToleranceDecimals) : ""
        }`;
      } else {
        return `${lo.value.toFixed(numDecimals)}-${hi.value.toFixed(
          numDecimals,
        )}`;
      }
    })
    .filter((part): part is string => part !== null);

  if (parts.length === 0) return "-";

  return parts.join(" x ") + (hasConvertedParts ? " " + unit : "");
};

// Federal QQ specs come first: "AMS-QQ-A-225/6" (or a vendor's "QQ A225/6")
// must parse as one token, not as a bare "AMS" prefix.
export const STANDARD_TOKEN_REGEX =
  /\b(?:(?:AMS[\s-]?)?QQ[\s-]?[A-Z][\s-]?\d+(?:[/-]\d+)*|AMS[\s-]?\d+[A-Z0-9-]*|ASTM[\s-]?[A-Z]?\d+[A-Z0-9-]*|ISO[\s-]?\d+[A-Z0-9-]*|UNS[\s-]?[A-Z]?\d+[A-Z0-9-]*|MIL[\s-]?[A-Z0-9-]+)\b/gi;

/**
 * Suppliers cite a family of specs from one body as a list under a single
 * heading — "AMS 5596, 5662, 5663, 5832; ASTM B637, B670" — where only the
 * first number carries the body. Every matcher here looks for "AMS 5662" as
 * one token, so the bare "5662" was invisible and the product page told the
 * buyer the paperwork does not name AMS 5662 while showing it on the same
 * screen. This writes the body back onto each member. Mirrors
 * functions/src/extraction/standard-normalization.ts.
 */
const SHORTHAND_BODY =
  "(AMS|ASTM|ASME|SAE|AWS|API|UNS|EN|DIN|ISO|JIS|BS|NACE|GE|PWA|QQ|MIL(?:-[A-Z]+)?)";
const SHORTHAND_FIRST =
  "([A-Z]{0,3}[\\s-]?\\d[\\dA-Z]*(?:[/-]\\d+)?[A-Z]?(?:\\s+Gr\\.?\\s*\\d+[A-Z]?)?)";
const SHORTHAND_MEMBER =
  "(?!(?:AMS|ASTM|ASME|SAE|AWS|API|UNS|EN|DIN|ISO|JIS|BS|NACE|GE|PWA|QQ|MIL)[\\s-])(?:[A-Z]{1,3}[\\s-]?\\d[\\dA-Z]*(?:[/-]\\d+)?[A-Z]?|\\d{3,5}[A-Z]?)(?:\\s+Gr\\.?\\s*\\d+[A-Z]?)?";
const SHORTHAND_LIST = new RegExp(
  `\\b${SHORTHAND_BODY}[\\s-]*${SHORTHAND_FIRST}((?:\\s*,\\s*${SHORTHAND_MEMBER})+)(?!\\w)`,
  "gi",
);

export const expandStandardShorthand = (raw: string): string => {
  if (!raw) return raw;
  return raw.replace(
    SHORTHAND_LIST,
    (_whole, body: string, first: string, rest: string) => {
      const members = rest
        .split(",")
        .map((m) => m.trim())
        .filter(Boolean);
      return [first.trim(), ...members].map((m) => `${body} ${m}`).join(", ");
    },
  );
};

const dedupeStandardTokens = (matches: string[]): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const match of matches) {
    const normalized = match.replace(/\s+/g, " ").trim();
    const dedupeKey = normalized.toLowerCase();
    if (!normalized || seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);
    unique.push(normalized);
  }
  return unique;
};

/**
 * A stored `standard` that names several specs may have been joined with bare
 * spaces at ingestion ("ASTM B211 QQ A225/9 AMS 4123"), which reads as one
 * garbled citation — a buyer comparing it against the spec on their drawing
 * concludes the item is certified to something slightly different when it
 * carries their spec verbatim. When the recognized spec tokens reconstruct the
 * whole string, show them comma separated; any other wording (revision notes,
 * "customer spec on file", already-delimited lists) is left exactly as stored.
 */
export const formatStandardList = (standard: string | undefined): string => {
  const text = (standard || "").trim();
  if (!text || /[,;]/.test(text)) return text;
  const tokens = extractStandardTokensFromText(text);
  if (tokens.length < 2) return text;
  const compact = (value: string) => value.replace(/[\s-]+/g, "");
  if (tokens.map(compact).join("") !== compact(text)) return text;
  return tokens.join(", ");
};

export const extractStandardTokensFromText = (
  ...parts: (string | undefined)[]
): string[] => {
  const candidates = parts
    .filter(
      (value): value is string =>
        typeof value === "string" && value.trim().length > 0,
    )
    .join(" ");
  if (!candidates) {
    return [];
  }
  return dedupeStandardTokens(
    expandStandardShorthand(candidates).match(STANDARD_TOKEN_REGEX) || [],
  );
};

export const extractMaterialArray = (p: Product) => {
  if (typeof p.material === "string") {
    const result = [];
    if (p.material) result.push(p.material);
    if (p.alloy) result.push(p.alloy);
    if (p.grade) result.push(p.grade);
    return result;
  }
  if (
    p.material &&
    Array.isArray(p.material.dbMaterials) &&
    p.material.dbMaterials.length > 0
  ) {
    return p.material.dbMaterials;
  }
  if (p.material?.text) {
    return Object.values(p.material.text).filter(
      (value): value is string =>
        typeof value === "string" && value.trim().length > 0,
    );
  }
  return [];
};

export const extractMaterialString = (p: Product) => getMaterialDisplayLabel(p);

// A dbMaterials entry that names a specification rather than an alloy
// ("AMS5640", "ASTM A484", "QQ-S-763"). Mirrors product-summary.ts.
const isSpecCodeMaterial = (m: string): boolean => {
  const compact = m.replace(/\s+/g, "");
  return (
    /^(ams|astm|uns|mil|iso|en|din|sae|aisi|jis|qq)\b/i.test(compact) ||
    /^[A-Za-z]{1,4}\d{3,}[A-Za-z0-9-]*$/.test(compact)
  );
};

/** Best-effort material label when dbMaterials is empty (e.g. unassigned Fry Steel rows). */
export const getMaterialDisplayLabel = (p: Product): string => {
  const fromDb = extractMaterialArray(p);
  if (fromDb.length > 0) {
    // When every entry is a spec code the alloy is in the vendor's grade
    // field ("15-5" behind ["AMS5659"]): show the alloy, then the specs.
    const grade =
      typeof p.material === "object" ? p.material?.grade?.trim() : undefined;
    if (grade && fromDb.every((m) => isSpecCodeMaterial(m))) {
      return [grade, ...fromDb.map(friendlyMaterialName)].join(", ");
    }
    return fromDb.map(friendlyMaterialName).join(", ");
  }
  const specHints = extractStandardTokensFromText(p.condition);
  if (specHints.length > 0) {
    return specHints.join(", ");
  }
  return "";
};

/**
 * Rewrites the size inside a title into the unit the buyer is looking at.
 *
 * Titles are written once, when an item is ingested, in whatever unit the vendor
 * published — so a buyer who switched to metric saw the number under the title
 * change to 38 mm while the title still read "1.5 in thick". Only a number we can
 * match against a dimension we actually hold is touched: anything we cannot
 * verify is left exactly as the vendor wrote it, because a title is also how a
 * buyer recognises the item.
 */
export const retitleForUnit = (
  title: string,
  dimensions: ProductDimensions | undefined,
  unit: string,
): string => {
  if (!title || !dimensions) return title;
  let out = title;
  for (const key of Object.keys(dimensions)) {
    const range = asDimensionRange(dimensions[key]);
    if (!range) continue;
    const from = range.lo?.unit;
    const value = range.lo?.value;
    if (!from || typeof value !== "number" || from === unit) continue;
    // Particle sizes (µm) have no sensible reading in the buyer's length unit,
    // so the title keeps them exactly as the vendor wrote them.
    if (isMicroUnit(from)) continue;
    // Ranges have two numbers in play and no reliable way to tell which of them
    // the title is quoting, so they are left alone.
    if (range.hi?.value !== value) continue;

    const converted = convertDimensionUnits(from, unit, value);
    const decimals = unit === "mm" ? 0 : 2;
    const replacement = `${trimNumber(converted.toFixed(decimals))} ${unit}`;
    for (const pattern of numberPatterns(value, from)) {
      if (pattern.test(out)) {
        out = out.replace(pattern, replacement);
        break;
      }
    }
  }
  return out;
};

/** "38.00" -> "38", "1.50" -> "1.5": trailing zeros read as false precision. */
const trimNumber = (value: string): string =>
  value.includes(".") ? value.replace(/\.?0+$/, "") : value;

/**
 * The ways a vendor might have written this measurement, most specific first —
 * `1.500 in`, `1.5"`, `1.5 inch`. Built from the value we hold rather than from
 * whatever number happens to sit in the title, so we never convert a quantity,
 * a grade or a spec number by mistake.
 */
const numberPatterns = (value: number, unit: string): RegExp[] => {
  const written = new Set<string>();
  for (const decimals of [0, 1, 2, 3]) {
    const fixed = value.toFixed(decimals);
    if (Number(fixed) !== value) continue;
    written.add(fixed);
    written.add(trimNumber(fixed));
  }
  const unitWords: Record<string, string[]> = {
    in: ["in", "inch", "inches", '"'],
    ft: ["ft", "foot", "feet", "'"],
    mm: ["mm"],
    cm: ["cm"],
    m: ["m", "meter", "meters", "metre", "metres"],
  };
  const words = unitWords[unit] || [unit];
  const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const patterns: RegExp[] = [];
  const ordered = Array.from(written).sort((a, b) => b.length - a.length);
  for (const number of ordered) {
    for (const word of words) {
      patterns.push(
        new RegExp(`${escape(number)}\\s*${escape(word)}(?![a-z0-9])`, "i"),
      );
    }
  }
  return patterns;
};

/** Page/list title when AI summary is missing. */
export const getProductDisplayTitle = (p: Product): string => {
  if (p.summary?.trim()) {
    return p.summary.trim();
  }
  const material = getMaterialDisplayLabel(p);
  const shape = p.shape?.trim() || "";
  const combined = [material, shape].filter(Boolean).join(" ");
  if (combined) {
    return combined;
  }
  if (p.condition?.trim()) {
    return [shape, p.condition.trim()].filter(Boolean).join(" — ");
  }
  return p.id || "Product";
};

/** The title as it should read for the unit currently selected. */
export const getProductDisplayTitleInUnit = (
  p: Product,
  unit: string,
): string => retitleForUnit(getProductDisplayTitle(p), p.dimensions, unit);

/** Shapes a buyer expects to be told the width and length of. */
const FLAT_SHAPES = ["sheet", "plate", "foil", "flat bar", "strip"];

/**
 * Why a flat product shows a thickness and nothing else.
 *
 * Mill stock like this is listed by thickness and cut when ordered, so there is
 * no sheet size to quote — a buyer working out how many parts they can nest was
 * reading the gap as missing data and had no way to know the answer is "tell us
 * the size you want".
 */
export const missingFlatSizeNote = (p: Product): string | null => {
  const shape = (p.shape || "").trim().toLowerCase();
  if (!FLAT_SHAPES.includes(shape)) return null;
  const keys = Object.keys(p.dimensions || {});
  if (keys.length === 0) return null;
  const hasThickness = keys.includes("thickness");
  const hasFace = keys.includes("width") || keys.includes("length");
  if (!hasThickness || hasFace) return null;
  return (
    "Only the thickness is published for this item: the supplier cuts it to " +
    "order, so there is no fixed sheet size. Tell us the width and length you " +
    "need in the notes when you add it to the cart and we will quote that cut."
  );
};

/**
 * How what the buyer asked for compares with what this item is certified to.
 *
 * We match on the alloy and rank specs, which is deliberate — but it meant an
 * item could come back against a query naming specs it does not carry, with
 * nothing on the page saying so.
 */
export type StandardsComparison = {
  requested: string[];
  missing: string[];
  matched: string[];
};

export const compareStandards = (
  p: Product,
  requested: string[],
): StandardsComparison | null => {
  if (requested.length === 0) return null;
  const carried = canonicalStandardSet(p);
  const matched: string[] = [];
  const missing: string[] = [];
  for (const token of requested) {
    const satisfied = requestAliases(token).some((alias) => carried.has(alias));
    if (satisfied) {
      matched.push(token);
    } else {
      missing.push(token);
    }
  }
  return { requested, missing, matched };
};

/** Every canonical form a requested spec answers to: itself, its federal-spec
 *  twin, and its base designation with any revision stripped. */
const requestAliases = (token: string): string[] => {
  const aliases = [...federalSpecAliases(canonicalStandard(token))];
  const base = specRevisionBase(token);
  if (base) aliases.push(canonicalStandard(base));
  return aliases;
};

// Slashes are part of federal spec numbering ("QQ-A-225/6"), so they have to
// canonicalize away just like spaces and hyphens do.
const canonicalStandard = (value: string): string =>
  value.toLowerCase().replace(/[\s\-/]+/g, "");

// "AMS-QQ-A-225/6" is SAE's adoption of the federal spec "QQ-A-225/6" — the
// same document, cited two ways. Each form answers to the other.
const federalSpecAliases = (canonToken: string): string[] =>
  canonToken.startsWith("amsqq")
    ? [canonToken, canonToken.slice(3)]
    : canonToken.startsWith("qq")
    ? [canonToken, `ams${canonToken}`]
    : [canonToken];

/**
 * A spec citation often carries a revision: "ASTM F269-60(2019)" is spec F269,
 * revision 60, reapproved 2019; "AMS 5662N" is revision N of AMS 5662. Buyers
 * search for the base spec ("astm f269"), so a revisioned citation must also
 * answer to its base form — before this, an item whose material is literally
 * named "ASTM F269-60(2019)" was flagged as not carrying "astm f269".
 * Federal QQ and MIL numbering embed dashes and slashes in the designation
 * itself ("QQ-A-225/6", "MIL-T-9047"), so those are deliberately left alone.
 */
const specRevisionBase = (rawToken: string): string | null => {
  if (/^(?:AMS[\s-]?)?QQ/i.test(rawToken)) return null;
  const match = rawToken.match(/^((?:ASTM|AMS|ISO|UNS)[\s-]?[A-Z]?\d+)/i);
  if (!match) return null;
  const base = match[1];
  return base.length < rawToken.trim().length ? base : null;
};

// An item whose vendor named both delivery conditions of a spec without saying
// which ships carries this note (written by the ingest guard). It names the
// specs without certifying to them, so it never counts as "carried".
// Mirrors functions/src/extraction/standard-normalization.ts.
const CLARIFICATION_NOTE_RE =
  /This item can be [A-Z]+[\s-]?\d+[A-Z]? or [A-Z]+[\s-]?\d+[A-Z]?, please contact us if you need one of these standards\.?/gi;

export const stripConditionClarificationNote = (text: string): string =>
  (text || "").replace(CLARIFICATION_NOTE_RE, " ");

const specText = (p: Product): string =>
  stripConditionClarificationNote(p.additionalSpecifications || "");

const canonicalStandardSet = (p: Product): Set<string> => {
  const text = [
    p.standard || "",
    p.summary || "",
    specText(p),
    ...extractMaterialArray(p),
  ].join(" ");
  const carried = new Set<string>();
  for (const token of extractStandardTokensFromText(text)) {
    carried.add(canonicalStandard(token));
    // "ASTM F269-60" also answers to "ASTM F269".
    const base = specRevisionBase(token);
    if (base) carried.add(canonicalStandard(base));
  }
  return carried;
};

// Known wrought-aluminum grade numbers we are confident about. Used to (a) show
// a friendly "Aluminum 6061" label and (b) treat temper suffixes as the same
// base alloy when matching. Restricted to a curated set so we never mislabel a
// 4-digit steel code (e.g. 4130/4140/4340) as aluminum.
const ALUMINUM_GRADE_NUMBERS = new Set([
  "1100",
  "2011",
  "2024",
  "3003",
  "5052",
  "5083",
  "6061",
  "6063",
  "6082",
  "7050",
  "7075",
]);

// Strips an aluminum temper designation ("-T6", "-T651", "-O", "-H14", ...) and
// returns the bare grade number when the base is a known aluminum grade, so
// "6061-T6" and "6061" are treated as the same alloy. Leaves anything else as-is.
export const stripAluminumTemper = (code: string): string => {
  const normalized = (code || "").trim().toLowerCase();
  const match = normalized.match(
    /^(\d{4})[\s-]*(?:t\d+[a-z0-9]*|o|h\d+[a-z0-9]*|f|w)$/,
  );
  if (match && ALUMINUM_GRADE_NUMBERS.has(match[1])) {
    return match[1];
  }
  return normalized;
};

// Friendly display name for a single material code (e.g. "6061" -> "Aluminum
// 6061"). Non-aluminum codes (Inconel 625, AMS5662, ...) are returned unchanged.
export const friendlyMaterialName = (code: string): string => {
  const base = stripAluminumTemper(code);
  if (ALUMINUM_GRADE_NUMBERS.has(base)) {
    return `Aluminum ${code}`;
  }
  return code;
};

/**
 * Family words a buyer's query or a record's text can carry, mapped to the
 * family label stamped on records at publish time (`materialFamily`). Must
 * mirror functions/src/material-broad-family.ts so client-side ranking agrees
 * with the backend for family queries like "aluminum" or "stainless".
 */
const FAMILY_WORDS: Array<[RegExp, string]> = [
  [/\balumin(?:um|ium)\b/i, "aluminum"],
  [/\bstainless\b/i, "stainless steel"],
  [/\btitanium\b/i, "titanium"],
  [
    /\b(?:inconel|incoloy|monel|hastelloy|waspaloy|nimonic|nickel)\b/i,
    "nickel alloy",
  ],
  [/\b(?:haynes|stellite|cobalt)\b/i, "cobalt alloy"],
  [/\bbrass\b/i, "brass"],
  [/\bcopper\b/i, "copper"],
];

export const familiesReferencedInQuery = (query: string): string[] => {
  const families = new Set<string>();
  for (const [pattern, family] of FAMILY_WORDS) {
    if (pattern.test(query)) families.add(family);
  }
  return Array.from(families);
};

export const isMaterialMatch = (
  p: Product & { isExactMaterialMatch?: boolean },
  material: string,
) => {
  if (p.isExactMaterialMatch) {
    return true;
  }
  const target = material.toLowerCase();
  const targetBase = stripAluminumTemper(material);
  return extractMaterialArray(p).some((m) => {
    const candidate = m.toLowerCase();
    return candidate === target || stripAluminumTemper(m) === targetBase;
  });
};

export const someDimensionsMismatch = (
  p: Product,
  queryStandardDimensions: { [key: string]: { value: number; unit: string } },
  options: {
    productSmallerConsideredMismatch?: boolean;
    productLargerConsideredMismatch?: boolean;
    toleranceMm?: number; // tolerance in mm to account for minor discrepancies in dimension reporting
  } = {
    productSmallerConsideredMismatch: true,
    productLargerConsideredMismatch: true,
    toleranceMm: 0.02,
  },
) => {
  if (
    !options.productSmallerConsideredMismatch &&
    !options.productLargerConsideredMismatch
  ) {
    throw new Error(
      "At least one of productSmallerConsideredMismatch or productLargerConsideredMismatch must be true",
    );
  }
  return Object.entries(queryStandardDimensions).some(([key, v]) => {
    console.log("key", key, "value", v);
    if (!p.standardDimensions || !p.standardDimensions[key]) return false;
    return (
      (options.productSmallerConsideredMismatch &&
        v.value <
          p.standardDimensions[key].lo.value - (options.toleranceMm || 0.02)) ||
      (options.productLargerConsideredMismatch &&
        v.value >
          p.standardDimensions[key].hi.value + (options.toleranceMm || 0.02))
    );
  });
};

export const DIFFERENT_MATERIAL = "different-material";
export const DIFFERENT_DIMENSIONS = "different-dimensions";
export const DIFFERENT_MATERIAL_AND_DIMENSIONS =
  "different-material-and-dimensions";
export const GOOD_MATCH = "good-match";
export const SMALLER_DIMENSIONS = "smaller-dimensions";

const EQUIVALENT_ALLOY_NAMES: Record<string, string[]> = {
  "718": [
    "718",
    "alloy 718",
    "nickel 718",
    "718 superalloy",
    "inconel 718",
    "uns n07718",
    "n07718",
    "2.4668",
    "nicrofer 5219 nb",
    "haynes 718",
    "ati 718",
    "pyromet 718",
    "alvac 718",
    "altemp 718",
    "gh4169",
    "gh169",
    "ams 5662",
    "ams 5663",
    "ams 5596",
    "ams 5597",
    "ams 5589",
    "ams 5590",
    "astm b637",
    "asme sb637",
  ],
  "625": [
    "625",
    "alloy 625",
    "inconel 625",
    "uns n06625",
    "n06625",
    "2.4856",
    "nicr22mo9nb",
    "nicr22mo9cb",
    "haynes 625",
    "vdm alloy 625",
    "altemp 625",
    "nicrofer 6020",
    "iso nw6625",
    "ams 5666",
    "ams 5599",
    "astm b443",
    "astm b444",
    "astm b446",
  ],
  "600": [
    "600",
    "alloy 600",
    "inconel 600",
    "uns n06600",
    "n06600",
    "2.4816",
    "nicr15fe",
    "nicrofer 7216",
    "nickel 600",
    "nickelvac 600",
    "ferrochronin 600",
    "chronin 600",
    "na14",
  ],
};

type EquivalentAlloySignals = {
  families: string[];
  aliases: string[];
};

const normalizeForAliasMatching = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const containsAlias = (
  normalizedText: string,
  normalizedAlias: string,
): boolean => ` ${normalizedText} `.includes(` ${normalizedAlias} `);

const escapeRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const containsStandardShorthandToken = (
  normalizedText: string,
  token: string,
): boolean => {
  const escapedToken = escapeRegex(token.toLowerCase());
  const pattern = new RegExp(`\\b(?:[a-z]+[\\s-]?)?${escapedToken}\\b`, "i");
  return pattern.test(normalizedText);
};

// Decimal measurements written into product text or a query: ".625", "0.625",
// "1.625", "1.76\"". Alias normalization strips punctuation, so left in place
// a "monel 400 1.625" query and every 1.625" bar of any alloy both read as
// alloy 625, share a family, and the wrong alloys get a full-green material
// match ahead of the grade the buyer named. Werkstoff numbers (1.4404, 2.4856)
// are legitimate aliases and are kept: exactly four digits after the point,
// leading 1-3. Mirrors stripDecimalMeasurements in functions/src/search.ts.
const DECIMAL_MEASUREMENT_REGEX = /(?<![a-z0-9.])(\d*\.\d+)(?![a-z0-9])/gi;
const WERKSTOFF_NUMBER_REGEX = /^[123]\.\d{4}$/;

export const stripDecimalMeasurements = (text: string): string =>
  text.replace(DECIMAL_MEASUREMENT_REGEX, (match) =>
    WERKSTOFF_NUMBER_REGEX.test(match) ? match : " ",
  );

const getEquivalentAlloySignals = (text: string): EquivalentAlloySignals => {
  const normalizedText = normalizeForAliasMatching(
    stripDecimalMeasurements(text),
  );
  if (!normalizedText) {
    return { families: [], aliases: [] };
  }

  const families = new Set<string>();
  const aliases = new Set<string>();
  for (const [family, rawAliases] of Object.entries(EQUIVALENT_ALLOY_NAMES)) {
    for (const rawAlias of rawAliases) {
      const normalizedAlias = normalizeForAliasMatching(rawAlias);
      if (!normalizedAlias) continue;
      if (!containsAlias(normalizedText, normalizedAlias)) continue;
      families.add(family);
      aliases.add(normalizedAlias);
    }
  }

  return {
    families: Array.from(families),
    aliases: Array.from(aliases),
  };
};

// Broad alloy-family names that denote a family rather than a specific grade
// (e.g. "inconel" covers 718 / 625 / 600). A query like "inconel aged" carries
// no specific grade, so we treat family membership — not an exact grade — as the
// material match, keeping every Inconel item a good match while excluding
// unrelated alloys.
const GENERIC_ALLOY_FAMILY_TOKENS = [
  "inconel",
  "incoloy",
  "hastelloy",
  "monel",
  "nimonic",
  "waspaloy",
  "rene",
  "haynes",
  "stellite",
  "invar",
  "kovar",
  "nitronic",
];

const getQueryGenericFamilyTokens = (
  query: string,
  hasSpecificAlloyFamily: boolean,
): string[] => {
  // A specific grade (e.g. "inconel 718") is handled by the precise equivalence
  // logic; only fall back to the broad family when no specific grade is present.
  if (hasSpecificAlloyFamily) return [];
  const normalized = normalizeForAliasMatching(query || "");
  if (!normalized) return [];
  return GENERIC_ALLOY_FAMILY_TOKENS.filter((token) =>
    containsAlias(normalized, token),
  );
};

// Alloys we know to be age-hardenable, keyed by condition. A bare condition
// query (e.g. "aged") with no named material should still treat these alloy
// families as (imperfect) candidates — even when nothing is currently in that
// temper — so Inconel 718 shows as a not-perfect match rather than "no results".
// Any item actually in the temper ranks above them as a perfect match. Extend
// this map as we learn that other alloys are age-hardenable.
const CONDITION_IMPLIED_FAMILY_TOKENS: Record<string, string[]> = {
  aged: ["718", "inconel 718"],
};

const getConditionImpliedFamilyTokens = (
  condition: MaterialCondition | null,
  hasExplicitMaterialSignal: boolean,
): string[] => {
  if (!condition || hasExplicitMaterialSignal) return [];
  return CONDITION_IMPLIED_FAMILY_TOKENS[condition] || [];
};

// Query-side variant of STANDARD_TOKEN_REGEX (above): here the trailing number
// is optional so a bare standard family typed in the search box (e.g. "AMS",
// "ASTM") still matches. Kept separate from the display-label regex, which
// requires a number to avoid surfacing bare families as product specs.
const STANDARD_QUERY_TOKEN_REGEX =
  /\b(?:(?:AMS[\s-]?)?QQ[\s-]?[A-Z][\s-]?\d+(?:[/-]\d+)*|AMS(?:[\s-]?\d+[A-Z0-9-]*)?|ASTM(?:[\s-]?[A-Z]?\d+[A-Z0-9-]*)?|ISO(?:[\s-]?\d+[A-Z0-9-]*)?|UNS(?:[\s-]?[A-Z]?\d+[A-Z0-9-]*)?|MIL(?:[\s-]?[A-Z0-9-]+)?)\b/gi;

const extractStandardTokens = (text: string): string[] => {
  const matches = text.match(STANDARD_QUERY_TOKEN_REGEX) || [];
  const deduped = new Set<string>();
  const normalized: string[] = [];
  for (const token of matches) {
    const t = token.replace(/\s+/g, " ").trim();
    const key = t.toLowerCase();
    if (deduped.has(key)) continue;
    deduped.add(key);
    normalized.push(t);
  }
  return normalized;
};

const getQueryStandards = (structuredQuery: any): string[] => {
  const rawQuery =
    typeof structuredQuery?.rawQuery === "string"
      ? structuredQuery.rawQuery
      : "";
  return extractStandardTokens(rawQuery);
};

const extractSingleStandardShorthandToken = (
  structuredQuery: any,
): string | null => {
  const rawQuery =
    typeof structuredQuery?.rawQuery === "string"
      ? structuredQuery.rawQuery.trim().toLowerCase()
      : "";
  if (/^\d{4,5}[a-z0-9-]*$/.test(rawQuery)) {
    return rawQuery;
  }
  return null;
};

const getProductStandardCorpus = (p: Product): string => {
  return expandStandardShorthand(
    [
      p.summary || "",
      specText(p),
      p.standard || "",
      ...(extractMaterialArray(p) || []),
    ]
      .filter((value) => !!value)
      .join(" "),
  ).toLowerCase();
};

// --- material condition / temper (aged vs annealed) --------------------------
// Mirrors functions/src/search.ts: precipitation-hardenable alloys such as 718
// ship ANNEALED (solution annealed, AMS 5662) or AGED (precipitation hardened,
// AMS 5663). We infer condition from explicit words first (vendors like High
// Temp Metals stamp every bar with one spec package regardless of temper) then
// from the companion spec number.
type MaterialCondition = "aged" | "annealed";

// "STA" (solution treated + aged) and a bare "H/T" (heat treated) only *suggest*
// aging and need cert confirmation, so they are intentionally omitted here: such
// items stay condition-unknown and surface for an "aged" search as imperfect
// (not-green) matches. Explicit "aged" / "precipitation" wording still wins.
const AGED_CONDITION_REGEX =
  /\baged\b|age[\s-]?harden|precipitation|\bprecip\b|\bh(?:900|1025|1075|1100|1150)\b/i;
const ANNEALED_CONDITION_REGEX =
  /\bannealed\b|solution[\s-]?anneal|mill[\s-]?anneal/i;

// AMS5663/64 = aged Alloy 718; AMS5668 = aged Alloy X-750.
const AGED_SPEC_CANONS = ["ams5663", "ams5664", "ams5668"];
const ANNEALED_SPEC_CANONS = ["ams5662", "ams5596", "ams5589"];
// Annealed<->aged companion specs per form: requesting one is satisfied by its
// companion (same base spec, differing only by temper); the condition check
// enforces the aged-vs-annealed distinction separately.
const SPEC_CONDITION_GROUPS: string[][] = [
  ["ams5662", "ams5663"], // 718 bar / forging
  ["ams5596", "ams5597"], // 718 sheet / strip / plate
  ["ams5589", "ams5590"], // 718 seamless tube
];

// Shorthand spec lists ("AMS 5596, 5662, 5663") are written out in full first
// so every member is findable as its own "ams5662" once punctuation is gone.
const canonicalizeStandardText = (text: string): string =>
  expandStandardShorthand(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const companionSpecCanons = (canonToken: string): string[] => {
  for (const group of SPEC_CONDITION_GROUPS) {
    if (group.includes(canonToken)) return group;
  }
  return [canonToken];
};

const inferMaterialCondition = (text: string): MaterialCondition | null => {
  if (!text) return null;
  if (AGED_CONDITION_REGEX.test(text)) return "aged";
  if (ANNEALED_CONDITION_REGEX.test(text)) return "annealed";
  const canon = canonicalizeStandardText(text);
  const aged = AGED_SPEC_CANONS.some((spec) => canon.includes(spec));
  const annealed = ANNEALED_SPEC_CANONS.some((spec) => canon.includes(spec));
  if (aged && !annealed) return "aged";
  if (annealed && !aged) return "annealed";
  return null;
};

const getProductConditionCorpus = (p: Product): string =>
  [
    p.summary || "",
    p.additionalSpecifications || "",
    p.standard || "",
    p.condition || "",
    ...(extractMaterialArray(p) || []),
  ]
    .filter((value) => !!value)
    .join(" ");

// True when the query names a condition (e.g. "aged" / AMS 5663) and the product
// does not clearly share it (differs OR is unknown).
const hasRequestedConditionMismatch = (
  p: Product,
  structuredQuery: any,
): boolean => {
  const queryCondition = inferMaterialCondition(
    structuredQuery?.rawQuery || "",
  );
  if (queryCondition === null) {
    return false;
  }
  return (
    inferMaterialCondition(getProductConditionCorpus(p)) !== queryCondition
  );
};

const hasRequestedStandardMismatch = (
  p: Product,
  structuredQuery: any,
): boolean => {
  const queryStandards = getQueryStandards(structuredQuery);
  const queryStandardShorthand =
    extractSingleStandardShorthandToken(structuredQuery);
  const corpus = getProductStandardCorpus(p);
  const canonCorpus = canonicalizeStandardText(corpus);
  if (queryStandards.length === 0) {
    if (!queryStandardShorthand) {
      return false;
    }
    return !containsStandardShorthandToken(corpus, queryStandardShorthand);
  }
  const tokenMismatch = queryStandards.some((standard) => {
    const group = federalSpecAliases(
      canonicalizeStandardText(standard),
    ).flatMap((alias) => companionSpecCanons(alias));
    return !group.some((spec) => canonCorpus.includes(spec));
  });
  return (
    tokenMismatch ||
    (!!queryStandardShorthand &&
      !containsStandardShorthandToken(corpus, queryStandardShorthand))
  );
};

// ---------------------------------------------------------------------------
// Match & Ranking Rules (operator-configurable)
//
// Mirrors functions/src/match-ranking-rules.ts. The server includes its active
// rules in every search response; setActiveMatchRankingRules() adopts them so
// the badge and the client-side ordering use exactly the values the backend
// ranked with. Until a response arrives we fall back to the same defaults.
//
// Model: the 3-bar Match Quality badge has 6 steps (each bar degrades
// green -> yellow -> red, rightmost bar first). Each violated rule adds
// `levels` steps. Ranking compares priority groups (1 = most important, ties
// allowed); rules sharing a priority trade off via their summed levels.
// ---------------------------------------------------------------------------

export const MATCH_BADGE_TOTAL_STEPS = 6;

export type MatchRankingRules = {
  material: { priority: number; levels: number };
  shape: { priority: number; levels: number };
  sizeOversized: {
    priority: number;
    tolerancePct: number;
    levelsWithin: number;
    levelsBeyond: number;
  };
  sizeUndersized: {
    priority: number;
    tolerancePct: number;
    levelsWithin: number;
    levelsBeyond: number;
  };
  sizeMissing: { priority: number; levels: number };
  standards: { priority: number; levels: number };
  price: { priority: number };
  inventory: { priority: number };
};

export const DEFAULT_MATCH_RANKING_RULES: MatchRankingRules = {
  material: { priority: 1, levels: 4 },
  shape: { priority: 2, levels: 3 },
  sizeOversized: {
    priority: 3,
    tolerancePct: 30,
    levelsWithin: 1,
    levelsBeyond: 3,
  },
  sizeUndersized: {
    priority: 3,
    tolerancePct: 30,
    levelsWithin: 2,
    levelsBeyond: 5,
  },
  sizeMissing: { priority: 3, levels: 3 },
  standards: { priority: 4, levels: 2 },
  price: { priority: 5 },
  inventory: { priority: 6 },
};

let activeMatchRankingRules: MatchRankingRules = DEFAULT_MATCH_RANKING_RULES;

/** Adopt the rules the backend ranked with (from the search response). */
export const setActiveMatchRankingRules = (rules: any): void => {
  if (!rules || typeof rules !== "object") return;
  // The server sanitizes before serving; merge defensively over defaults so a
  // missing field can never crash the badge/sort.
  activeMatchRankingRules = {
    ...DEFAULT_MATCH_RANKING_RULES,
    ...rules,
    sizeOversized: {
      ...DEFAULT_MATCH_RANKING_RULES.sizeOversized,
      ...(rules.sizeOversized || {}),
    },
    sizeUndersized: {
      ...DEFAULT_MATCH_RANKING_RULES.sizeUndersized,
      ...(rules.sizeUndersized || {}),
    },
  };
};

export const getActiveMatchRankingRules = (): MatchRankingRules =>
  activeMatchRankingRules;

// Keep in sync with functions/src/search.ts. Cross-section dominates; length is
// a cut-to-size afterthought so it must not drown out diameter/thickness.
const DIMENSION_MATCH_WEIGHTS: Record<string, number> = {
  diameter: 1,
  outer_diameter: 1,
  inner_diameter: 1,
  thickness: 1,
  wall_thickness: 1,
  width: 0.8,
  outer_width: 0.8,
  inner_width: 0.8,
  height: 0.8,
  outer_height: 0.8,
  inner_height: 0.8,
  length: 0.1,
};
const DEFAULT_DIMENSION_MATCH_WEIGHT = 0.5;
/** Exact size is perfect; within this relative gap is still a "good" match. */
export const DIMENSION_GOOD_MATCH_TOLERANCE = 0.3;

type DimensionFit = {
  comparedCount: number;
  missingCrossSectionCount: number;
  distanceScore: number;
  maxRelativeGap: number;
  undersized: boolean;
  undersizedBeyondTolerance: boolean;
  oversized: boolean;
  oversizedBeyondTolerance: boolean;
};

export const getDimensionFit = (
  p: Product,
  structuredQuery: any,
): DimensionFit => {
  const rules = getActiveMatchRankingRules();
  const oversizedTolerance = rules.sizeOversized.tolerancePct / 100;
  const undersizedTolerance = rules.sizeUndersized.tolerancePct / 100;
  const fit: DimensionFit = {
    comparedCount: 0,
    missingCrossSectionCount: 0,
    distanceScore: 0,
    maxRelativeGap: 0,
    undersized: false,
    undersizedBeyondTolerance: false,
    oversized: false,
    oversizedBeyondTolerance: false,
  };
  const queryDimensions = structuredQuery?.standardDimensions || {};
  for (const [key, queryValue] of Object.entries(queryDimensions)) {
    if (!isObjectWithValueUnit(queryValue)) continue;
    const pd = p.standardDimensions?.[key];
    if (!pd?.lo || !pd?.hi) {
      if (key !== "length") {
        fit.missingCrossSectionCount += 1;
      }
      continue;
    }
    fit.comparedCount += 1;
    const lo = convertDimensionUnits(pd.lo.unit, queryValue.unit, pd.lo.value);
    const hi = convertDimensionUnits(pd.hi.unit, queryValue.unit, pd.hi.value);
    let rawDistance = 0;
    if (queryValue.value < lo) {
      rawDistance = lo - queryValue.value;
    } else if (queryValue.value > hi) {
      rawDistance = queryValue.value - hi;
    }
    if (rawDistance === 0) continue;
    const denominator = Math.max(Math.abs(queryValue.value), 1e-6);
    const relativeGap = rawDistance / denominator;
    // Unit round-trips (in -> mm -> in) leave ~1e-16 phantom gaps that read
    // as a real mismatch ("undersized" on an exact 0.5 in thickness).
    if (relativeGap < 1e-9) continue;
    const weight =
      DIMENSION_MATCH_WEIGHTS[key] ?? DEFAULT_DIMENSION_MATCH_WEIGHT;
    fit.distanceScore += relativeGap * weight;
    // Longer stock is cut to the requested length, so a length overshoot is
    // not an off-size (mirrors functions/src/search.ts): it feeds only the
    // distance tiebreak. Undersized length stays a real mismatch.
    if (key === "length" && queryValue.value < lo) {
      continue;
    }
    fit.maxRelativeGap = Math.max(fit.maxRelativeGap, relativeGap);
    if (queryValue.value > hi) {
      fit.undersized = true;
      if (relativeGap > undersizedTolerance) {
        fit.undersizedBeyondTolerance = true;
      }
    } else {
      fit.oversized = true;
      if (relativeGap > oversizedTolerance) {
        fit.oversizedBeyondTolerance = true;
      }
    }
  }
  return fit;
};

/** 0 perfect, 1 within ±30%, 2 mismatch / wrong form, 3 undersized past tolerance. */
export const getDimensionFitBucket = (fit: DimensionFit): number => {
  if (fit.comparedCount === 0 && fit.missingCrossSectionCount === 0) {
    return 0;
  }
  if (fit.undersizedBeyondTolerance) {
    return 3;
  }
  if (fit.missingCrossSectionCount > 0) {
    return 2;
  }
  if (fit.maxRelativeGap === 0) {
    return 0;
  }
  return fit.maxRelativeGap <= DIMENSION_GOOD_MATCH_TOLERANCE ? 1 : 2;
};

export const getDimensionDirectionPenalty = (
  p: Product,
  structuredQuery: any,
) => (getDimensionFit(p, structuredQuery).undersized ? 1 : 0);

export const getDimensionDistanceScore = (
  p: Product,
  structuredQuery: any,
): number => getDimensionFit(p, structuredQuery).distanceScore;

const ROUND_CROSS_SECTION_SHAPE_TOKENS = ["bar", "rod", "tube", "pipe", "wire"];
const FLAT_CROSS_SECTION_SHAPE_TOKENS = ["sheet", "plate", "foil"];
// Flat rectangular stock is one family (mirrors functions/src/search.ts):
// which name an item carries is whoever wrote it down, and a "flat bar"
// request is filled by a plate cut down (and vice versa).
const FLAT_STOCK_FAMILY_SHAPES = [
  "plate",
  "sheet",
  "flat bar",
  "rectangular bar",
  "block",
];

/** Soft shape preference: lone diameter → round forms before 1"-thick sheet. */
export const getShapePreferenceRank = (
  p: Product,
  structuredQuery: any,
): number => {
  const product = (p.shape || "").toLowerCase().replace(/_/g, " ");
  const queryShape = (structuredQuery?.shape || "")
    .toLowerCase()
    .replace(/_/g, " ");
  if (queryShape && queryShape !== "unknown") {
    if (!product) return 1;
    if (product === queryShape) return 0;
    if (FLAT_STOCK_FAMILY_SHAPES.includes(queryShape)) {
      return FLAT_STOCK_FAMILY_SHAPES.includes(product) ||
        FLAT_CROSS_SECTION_SHAPE_TOKENS.some((t) => product.includes(t))
        ? 0
        : 1;
    }
    if (
      queryShape.includes("bar") &&
      ROUND_CROSS_SECTION_SHAPE_TOKENS.some((t) => product.includes(t))
    ) {
      return 0;
    }
    if (
      (queryShape.includes("sheet") || queryShape.includes("plate")) &&
      FLAT_CROSS_SECTION_SHAPE_TOKENS.some((t) => product.includes(t))
    ) {
      return 0;
    }
    return 1;
  }
  const dims = structuredQuery?.standardDimensions || {};
  const hasDiameter =
    !!dims.diameter || !!dims.outer_diameter || !!dims.inner_diameter;
  const hasThickness = !!dims.thickness || !!dims.wall_thickness;
  if (hasDiameter && !hasThickness) {
    return ROUND_CROSS_SECTION_SHAPE_TOKENS.some((t) => product.includes(t))
      ? 0
      : 1;
  }
  if (hasThickness && !hasDiameter) {
    // A thickness query means flat stock — the whole family, flat bars
    // included (mirrors functions/src/search.ts).
    return FLAT_STOCK_FAMILY_SHAPES.includes(product) ||
      FLAT_CROSS_SECTION_SHAPE_TOKENS.some((t) => product.includes(t))
      ? 0
      : 1;
  }
  return 0;
};

const isObjectWithValueUnit = (
  value: unknown,
): value is { value: number; unit: string } =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as any).value === "number" &&
  typeof (value as any).unit === "string";

// Material / standard / condition mismatch signals for a product vs the query.
// Shared by the legacy mismatch-type badge and the rules-based match scoring.
export const getMatchSignals = (
  p: Product & { isExactMaterialMatch?: boolean },
  structuredQuery: any,
) => {
  const queryAlloySignals = getEquivalentAlloySignals(
    structuredQuery?.rawQuery || "",
  );
  const productAlloySignals = getEquivalentAlloySignals(
    [
      p.summary || "",
      p.additionalSpecifications || "",
      ...(extractMaterialArray(p) || []),
    ].join(" "),
  );
  const hasEquivalentAlloyMatch = productAlloySignals.families.some((family) =>
    queryAlloySignals.families.includes(family),
  );

  // "unknown" / "none" are sentinels the query structuring returns when no grade
  // was named (e.g. a bare "aged" or generic "inconel" search); ignore them so
  // they aren't matched as a literal grade (which would flag everything yellow).
  const gradeRaw =
    typeof structuredQuery.grade === "string"
      ? structuredQuery.grade.trim()
      : "";
  const grade = ["unknown", "none"].includes(gradeRaw.toLowerCase())
    ? ""
    : gradeRaw;

  const queryGenericFamilies = getQueryGenericFamilyTokens(
    structuredQuery?.rawQuery || "",
    queryAlloySignals.families.length > 0,
  );
  const queryCondition = inferMaterialCondition(
    structuredQuery?.rawQuery || "",
  );
  const conditionImpliedFamilies = getConditionImpliedFamilyTokens(
    queryCondition,
    queryAlloySignals.families.length > 0 ||
      queryGenericFamilies.length > 0 ||
      !!grade,
  );
  const queryFamilyTokens = [
    ...queryGenericFamilies,
    ...conditionImpliedFamilies,
  ];
  const productMaterialCorpus = normalizeForAliasMatching(
    [
      p.summary || "",
      p.additionalSpecifications || "",
      ...(extractMaterialArray(p) || []),
    ].join(" "),
  );
  const hasFamilyTokenMatch =
    queryFamilyTokens.length > 0 &&
    queryFamilyTokens.some((token) =>
      containsAlias(productMaterialCorpus, token),
    );

  // Generic material families the query names ("aluminum", "stainless", ...),
  // matched against the `materialFamily` stamped on records at publish time or
  // a family word in the record's own text. Mirrors the backend reranker.
  const queryMaterialFamilies =
    queryAlloySignals.families.length > 0 || queryFamilyTokens.length > 0
      ? []
      : familiesReferencedInQuery(structuredQuery?.rawQuery || "");
  const productFamily = (p as any).materialFamily as string | undefined;
  const hasMaterialFamilyMatch =
    queryMaterialFamilies.length > 0 &&
    ((!!productFamily && queryMaterialFamilies.includes(productFamily)) ||
      familiesReferencedInQuery(
        [
          p.summary || "",
          p.additionalSpecifications || "",
          ...(extractMaterialArray(p) || []),
        ].join(" "),
      ).some((family) => queryMaterialFamilies.includes(family)));

  // A specific grade was named ("Titanium Grade 3"): the family word in the
  // same query ("titanium") must not make every other grade of the family an
  // equal match. Mirrors the backend reranker's `specificGradeNamed`.
  const specificGradeNamed = !!grade && /\d/.test(grade);
  const matMismatch =
    queryAlloySignals.families.length > 0 || specificGradeNamed
      ? !!grade && !isMaterialMatch(p, grade) && !hasEquivalentAlloyMatch
      : queryFamilyTokens.length > 0
      ? !hasFamilyTokenMatch
      : queryMaterialFamilies.length > 0
      ? !hasMaterialFamilyMatch
      : !!grade && !isMaterialMatch(p, grade);
  const standardMismatch = hasRequestedStandardMismatch(p, structuredQuery);
  const conditionMismatch = hasRequestedConditionMismatch(p, structuredQuery);

  return { matMismatch, standardMismatch, conditionMismatch };
};

export const getMismatchType = (
  p: Product & { isExactMaterialMatch?: boolean },
  structuredQuery: any,
) => {
  const { matMismatch, standardMismatch, conditionMismatch } = getMatchSignals(
    p,
    structuredQuery,
  );
  const smallerDims =
    structuredQuery.standardDimensions &&
    someDimensionsMismatch(p, structuredQuery.standardDimensions, {
      productSmallerConsideredMismatch: false,
      productLargerConsideredMismatch: true,
    });

  if (smallerDims) return SMALLER_DIMENSIONS;
  if (!matMismatch && !standardMismatch && !conditionMismatch)
    return GOOD_MATCH;
  if (matMismatch && standardMismatch) return DIFFERENT_MATERIAL_AND_DIMENSIONS;
  if (matMismatch || standardMismatch || conditionMismatch)
    return DIFFERENT_MATERIAL;
  return DIFFERENT_DIMENSIONS;
};

export const MISMATCH_GRADE = {
  [DIFFERENT_DIMENSIONS]: 10,
  [DIFFERENT_MATERIAL]: 20,
  [DIFFERENT_MATERIAL_AND_DIMENSIONS]: 30,
  [SMALLER_DIMENSIONS]: 30,
  [GOOD_MATCH]: 0,
};

// ---------------------------------------------------------------------------
// Rules-based match scoring — the ONE source of truth for both the badge and
// the client-side ordering, driven by the active MatchRankingRules.
// ---------------------------------------------------------------------------

export type MatchLevels = {
  material: number;
  shape: number;
  sizeOversized: number;
  sizeUndersized: number;
  sizeMissing: number;
  standards: number;
};

export type MatchAssessment = {
  levels: MatchLevels;
  /** Total badge degradation, clamped to MATCH_BADGE_TOTAL_STEPS. */
  totalLevels: number;
  dimensionDistanceScore: number;
};

/**
 * Score a product against the query using the active rules. Each violated rule
 * contributes its configured degradation levels; the same numbers order
 * products within a rule's ranking priority group, so what the buyer sees
 * (bars) always agrees with where the item ranks.
 */
export const getMatchAssessment = (
  p: Product & { isExactMaterialMatch?: boolean },
  structuredQuery: any,
): MatchAssessment => {
  const rules = getActiveMatchRankingRules();
  const { matMismatch, standardMismatch, conditionMismatch } = getMatchSignals(
    p,
    structuredQuery,
  );
  const fit = getDimensionFit(p, structuredQuery);
  const shapeRank = getShapePreferenceRank(p, structuredQuery);
  const levels: MatchLevels = {
    material: matMismatch ? rules.material.levels : 0,
    shape: shapeRank > 0 ? rules.shape.levels : 0,
    sizeOversized: fit.oversized
      ? fit.oversizedBeyondTolerance
        ? rules.sizeOversized.levelsBeyond
        : rules.sizeOversized.levelsWithin
      : 0,
    sizeUndersized: fit.undersized
      ? fit.undersizedBeyondTolerance
        ? rules.sizeUndersized.levelsBeyond
        : rules.sizeUndersized.levelsWithin
      : 0,
    sizeMissing:
      fit.missingCrossSectionCount > 0 ? rules.sizeMissing.levels : 0,
    standards:
      standardMismatch || conditionMismatch ? rules.standards.levels : 0,
  };
  const totalLevels = Math.min(
    MATCH_BADGE_TOTAL_STEPS,
    levels.material +
      levels.shape +
      levels.sizeOversized +
      levels.sizeUndersized +
      levels.sizeMissing +
      levels.standards,
  );
  return { levels, totalLevels, dimensionDistanceScore: fit.distanceScore };
};

export type MatchBarColor = "green" | "yellow" | "red";

/**
 * Render the total degradation as 3 bars, rightmost degrading first, each bar
 * stepping green -> yellow -> red. E.g. 2 levels => [green, green, red].
 */
export const getMatchBadgeBars = (totalLevels: number): MatchBarColor[] => {
  const d = Math.min(Math.max(totalLevels, 0), MATCH_BADGE_TOTAL_STEPS);
  // Bars left->right; rightmost bar absorbs the first 2 levels, then middle...
  return [2, 1, 0].map((barFromRight) => {
    const lost = Math.min(2, Math.max(0, d - 2 * (2 - barFromRight)));
    return lost === 0 ? "green" : lost === 1 ? "yellow" : "red";
  }) as MatchBarColor[];
};

const LEVEL_RULE_KEYS: (keyof MatchLevels)[] = [
  "material",
  "shape",
  "sizeOversized",
  "sizeUndersized",
  "sizeMissing",
  "standards",
];

type PriorityGroup = { priority: number; keys: string[] };

const buildPriorityGroups = (rules: MatchRankingRules): PriorityGroup[] => {
  const byPriority = new Map<number, string[]>();
  const allKeys = [...LEVEL_RULE_KEYS, "price", "inventory"];
  for (const key of allKeys) {
    const priority = (rules as any)[key].priority as number;
    byPriority.set(priority, [...(byPriority.get(priority) || []), key]);
  }
  return Array.from(byPriority.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([priority, keys]) => ({ priority, keys }));
};

export type RankableEntry = {
  assessment: MatchAssessment;
  comparablePrice: number;
  inventoryLevel: number;
  /** Backend relevance order, used as the final stable tiebreak. */
  originalIndex: number;
};

/**
 * Compare two products by the active rules: priority group by priority group
 * (1 = most important); rules sharing a priority trade off via summed levels;
 * price / inventory order whatever is still tied at their priority. Mirrors
 * the backend comparator in functions/src/search.ts.
 */
export const compareRankableEntries = (
  a: RankableEntry,
  b: RankableEntry,
): number => {
  const groups = buildPriorityGroups(getActiveMatchRankingRules());
  for (const group of groups) {
    let levelsA = 0;
    let levelsB = 0;
    let hasSizeRule = false;
    let hasPriceRule = false;
    let hasInventoryRule = false;
    for (const key of group.keys) {
      if (key === "price") {
        hasPriceRule = true;
      } else if (key === "inventory") {
        hasInventoryRule = true;
      } else {
        levelsA += a.assessment.levels[key as keyof MatchLevels];
        levelsB += b.assessment.levels[key as keyof MatchLevels];
        if (key.startsWith("size")) hasSizeRule = true;
      }
    }
    if (levelsA !== levelsB) return levelsA - levelsB;
    if (
      hasSizeRule &&
      a.assessment.dimensionDistanceScore !==
        b.assessment.dimensionDistanceScore
    ) {
      return (
        a.assessment.dimensionDistanceScore -
        b.assessment.dimensionDistanceScore
      );
    }
    if (hasPriceRule && a.comparablePrice !== b.comparablePrice) {
      return a.comparablePrice - b.comparablePrice;
    }
    if (hasInventoryRule && a.inventoryLevel !== b.inventoryLevel) {
      return b.inventoryLevel - a.inventoryLevel;
    }
  }
  return a.originalIndex - b.originalIndex;
};

export const rerankSearchResults = (
  searchResults: Product[],
  structuredQuery: any,
) => {
  return sortBy(searchResults, [
    (p) => {
      const mismatchType = getMismatchType(p, structuredQuery);
      return MISMATCH_GRADE[mismatchType as keyof typeof MISMATCH_GRADE];
    },
    (p) => getShapePreferenceRank(p, structuredQuery),
    (p) => getDimensionFitBucket(getDimensionFit(p, structuredQuery)),
    (p) => getDimensionDirectionPenalty(p, structuredQuery),
    (p) => getDimensionDistanceScore(p, structuredQuery),
    (p) => p.id,
  ]);
};

export const shapeIsBarTube = (shape: string): boolean => {
  const barShapes = ["bar", "rod", "tube", "pipe", "angle", "channel"];
  return (
    shape.toLocaleLowerCase().endsWith(" bar") ||
    shape.toLocaleLowerCase().endsWith(" tube") ||
    barShapes.includes(shape.toLowerCase())
  );
};

export const shapeIsSheet = (shape: string): boolean => {
  const sheetShapes = ["sheet", "plate"];
  return (
    shape.toLocaleLowerCase().endsWith(" sheet") ||
    sheetShapes.includes(shape.toLowerCase())
  );
};

// Powder ("metal powder", "gas atomized powder", ...) has no length or piece
// geometry: it is priced and bought by weight alone.
export const shapeIsPowder = (shape: string): boolean =>
  shape.toLowerCase().includes("powder");

export const isLengthUnit = (unit: string | undefined): boolean => {
  if (!unit) return false;
  const lengthUnits = ["mm", "cm", "m", "in", "ft"];
  return lengthUnits.includes(unit.toLocaleLowerCase());
};

export const isUnitUnit = (unit: string | undefined): boolean => {
  if (!unit) return false;

  const unitUnits = [
    "unit",
    "units",
    "pcs",
    "piece",
    "pieces",
    "bar",
    "bars",
    "sheet",
    "sheets",
    "plate",
    "plates",
  ];

  return unitUnits.includes(unit.toLocaleLowerCase());
};

export const isWeightUnit = (unit: string | undefined): boolean => {
  if (!unit) return false;
  const weightUnits = ["kg", "g", "lb", "oz"];
  return weightUnits.includes(unit.toLocaleLowerCase());
};

// Area units the price converters understand, in mm² per unit. Sheet/plate
// goods are often quoted by coverage ("10 SF"), so a per-piece price must be
// expressible per square foot and back. Keep in sync with
// functions/src/pricing-diagnostics.ts.
export const AREA_MM2_PER_UNIT: Record<string, number> = {
  sqft: 304.8 * 304.8,
  sqin: 25.4 * 25.4,
  sqm: 1_000_000,
};

export const isAreaUnit = (unit: string | undefined): boolean =>
  !!unit && AREA_MM2_PER_UNIT[unit.toLocaleLowerCase()] !== undefined;

// Maps common supplier spellings of a price unit onto the canonical tokens the
// conversion math understands ("lbs"/"pounds" -> "lb", "feet"/'"' -> ft, etc.).
// Keep in sync with functions/src/price-units.ts on the backend.
const PRICE_UNIT_SYNONYMS: { [key: string]: string } = {
  lb: "lb",
  lbs: "lb",
  pound: "lb",
  pounds: "lb",
  kg: "kg",
  kgs: "kg",
  kilo: "kg",
  kilos: "kg",
  kilogram: "kg",
  kilograms: "kg",
  g: "g",
  gram: "g",
  grams: "g",
  gm: "g",
  oz: "oz",
  ounce: "oz",
  ounces: "oz",
  mm: "mm",
  millimeter: "mm",
  millimeters: "mm",
  cm: "cm",
  centimeter: "cm",
  centimeters: "cm",
  m: "m",
  meter: "m",
  meters: "m",
  metre: "m",
  metres: "m",
  in: "in",
  inch: "in",
  inches: "in",
  '"': "in",
  ft: "ft",
  foot: "ft",
  feet: "ft",
  "'": "ft",
  // Area (sheet/plate goods quoted by coverage, e.g. "10 SF")
  sqft: "sqft",
  sf: "sqft",
  "sq ft": "sqft",
  "sq.ft": "sqft",
  "sq. ft": "sqft",
  "sq. ft.": "sqft",
  ft2: "sqft",
  "ft²": "sqft",
  "square foot": "sqft",
  "square feet": "sqft",
  sqin: "sqin",
  "sq in": "sqin",
  in2: "sqin",
  "in²": "sqin",
  "square inch": "sqin",
  "square inches": "sqin",
  sqm: "sqm",
  "sq m": "sqm",
  m2: "sqm",
  "m²": "sqm",
  "square meter": "sqm",
  "square meters": "sqm",
  "square metre": "sqm",
  "square metres": "sqm",
  unit: "unit",
  units: "unit",
  ea: "unit",
  each: "unit",
  pc: "unit",
  pcs: "unit",
  piece: "unit",
  pieces: "unit",
};

export const canonicalizePriceUnit = (unit: string | undefined): string => {
  const normalized = (unit || "").trim().toLocaleLowerCase();
  if (!normalized) return normalized;
  const stripped = normalized.startsWith("/")
    ? normalized.slice(1)
    : normalized;
  return (
    PRICE_UNIT_SYNONYMS[normalized] || PRICE_UNIT_SYNONYMS[stripped] || stripped
  );
};

// Tiny epsilon so exact quantities (e.g. 60.0 that arrives as 59.9999999 after
// unit conversions) floor to the intended whole unit rather than one below.
const AVAILABILITY_FLOOR_EPSILON = 1e-9;

/** Nominal weight (kg) of one bar/tube piece from geometry + density. */
export const getNominalBarPieceWeightKg = (product: Product): number | null => {
  if (!product.material?.density) return null;
  const volume = calculateMaterialVolume(product);
  if (!volume) return null;
  const kg = (volume * product.material.density) / 1e9;
  return kg > 0 ? kg : null;
};

/** On-hand stock weight divided by one nominal piece weight (geometry-based). */
export const getBarWeightStockRatio = (product: Product): number | null => {
  if (!shapeIsBarTube(product.shape || "")) return null;
  if (!isWeightUnit(product.quantity?.unit) || !product.quantity?.value) {
    return null;
  }
  const nominalKg = getNominalBarPieceWeightKg(product);
  if (!nominalKg) return null;
  const stockKg = normalizeWeightToKg({
    unit: product.quantity.unit,
    value: product.quantity.value,
  });
  return stockKg / nominalKg;
};

/** Bar/tube stocked by weight where on-hand is less than one nominal piece
 *  (common when a vendor lists mill length but stock is a short remnant). */
export const isBarWeightRemnant = (product: Product): boolean => {
  const ratio = getBarWeightStockRatio(product);
  return ratio !== null && ratio < 1 - AVAILABILITY_FLOOR_EPSILON;
};

// Numeric available length for a bar/tube expressed in `dimensionUnit`. This is
// the single source of truth shared by the "Quantity Available" display and the
// cart quantity validation, so the two can never disagree. Returns null when we
// lack the data needed to compute a physical length.
export const getBarAvailabilityInUnit = (
  product: Product,
  dimensionUnit: string,
): number | null => {
  // If the quantity is already given in a length unit, we can directly use it
  // and just convert it to the desired display length unit.
  if (isLengthUnit(product.quantity?.unit)) {
    return convertDimensionUnits(
      product.quantity?.unit as string,
      dimensionUnit,
      product.quantity?.value as number,
    );
  }

  // If the quantity is given in "units" (e.g., pieces), we need to convert it
  // to a length based on the standard dimensions of the product.
  if (
    isUnitUnit(product.quantity?.unit) &&
    product.standardDimensions?.length
  ) {
    const length = product.standardDimensions.length;
    const lengthPerPiece = convertDimensionUnits(
      length.hi.unit,
      dimensionUnit,
      length.hi.value,
    );
    return lengthPerPiece * (product.quantity?.value || 0);
  }

  // If the quantity is given in weight (e.g., kg), we can estimate the length
  // based on the density of the material and the standard dimensions.
  if (isWeightUnit(product.quantity?.unit) && product.material?.density) {
    const density = product.material.density; // in kg/m^3
    const volume = calculateMaterialVolume(product); // in mm^3
    if (volume && product.standardDimensions?.length) {
      const weightInKg = normalizeWeightToKg({
        unit: product.quantity!.unit,
        value: product.quantity!.value,
      });
      const unitWeightInKg = (volume * density) / 1e9; // convert mm^3 to m^3
      const numUnits = weightInKg / unitWeightInKg; // how many units of the product
      const unitLength = convertDimensionUnits(
        product.standardDimensions.length.hi.unit,
        dimensionUnit,
        product.standardDimensions.length.hi.value,
      );
      return unitLength * numUnits;
    }
  }

  // If we reach here, we don't have enough information to compute the length.
  return null;
};

/** Floor a fractional availability to a whole orderable count without
 *  under-cutting exact values that only look fractional due to float noise. */
export const floorOrderableQuantity = (value: number): number =>
  Math.max(0, Math.floor(value + AVAILABILITY_FLOOR_EPSILON));

/** Max whole units a buyer can order in `requestedUnit`, derived from physical
 *  stock (not a lossy price round-trip). Shared by the availability display and
 *  cart quantity validation so the two never disagree. Returns null when we lack
 *  the data to compute a cap (no max-quantity error is shown in that case). */
export const getMaxOrderableQuantity = (
  product: Product,
  requestedUnit: string,
): number | null => {
  if (!product.quantity?.value) {
    return null;
  }
  if (product.quantity.value <= 0) {
    return 0;
  }

  // Weight-stocked bar remnants are sold as the physical piece (priced from the
  // actual on-hand weight), not cut-to-length from a nominal mill-bar length.
  if (isBarWeightRemnant(product)) {
    const { value: qtyValue, unit: qtyUnit } = product.quantity;
    const normalizedRequested = canonicalizePriceUnit(requestedUnit);
    const normalizedQty = canonicalizePriceUnit(qtyUnit);
    if (isUnitUnit(normalizedRequested)) {
      return 1;
    }
    if (isWeightUnit(normalizedRequested) && isWeightUnit(normalizedQty)) {
      return floorOrderableQuantity(
        convertWeightUnits(normalizedQty, normalizedRequested, qtyValue),
      );
    }
    return null;
  }

  const { value: qtyValue, unit: qtyUnit } = product.quantity;
  const normalizedRequested = canonicalizePriceUnit(requestedUnit);
  const normalizedQty = canonicalizePriceUnit(qtyUnit);

  if (normalizedRequested === normalizedQty) {
    return floorOrderableQuantity(qtyValue);
  }

  if (isUnitUnit(normalizedRequested) && isUnitUnit(normalizedQty)) {
    return floorOrderableQuantity(qtyValue);
  }

  // Ordering discrete pieces from stock counted by weight (e.g. a leftover plate
  // stocked as "663 lb"). Derive how many whole pieces the weight represents from
  // the material density and the piece geometry. A remnant that is a fraction of a
  // nominal piece is still one physical piece the buyer can take, so we floor but
  // never drop below 1 when there is any stock.
  if (
    isUnitUnit(normalizedRequested) &&
    isWeightUnit(normalizedQty) &&
    product.material?.density
  ) {
    const volume = calculateMaterialVolume(product);
    if (volume) {
      const unitWeightKg = (volume * product.material.density) / 1e9;
      if (unitWeightKg > 0) {
        const totalWeightKg = normalizeWeightToKg({
          unit: qtyUnit,
          value: qtyValue,
        });
        const pieces = totalWeightKg / unitWeightKg;
        return Math.max(1, floorOrderableQuantity(pieces));
      }
    }
  }

  if (
    isLengthUnit(normalizedRequested) &&
    shapeIsBarTube(product.shape || "")
  ) {
    const available = getBarAvailabilityInUnit(product, normalizedRequested);
    if (available !== null) {
      return floorOrderableQuantity(available);
    }
  }

  if (isWeightUnit(normalizedRequested)) {
    if (isWeightUnit(normalizedQty)) {
      return floorOrderableQuantity(
        convertWeightUnits(normalizedQty, normalizedRequested, qtyValue),
      );
    }
    if (isUnitUnit(normalizedQty) && product.material?.density) {
      const volume = calculateMaterialVolume(product);
      if (volume) {
        const unitWeightKg = (volume * product.material.density) / 1e9;
        const totalWeightKg = unitWeightKg * qtyValue;
        return floorOrderableQuantity(
          convertWeightUnits("kg", normalizedRequested, totalWeightKg),
        );
      }
    }
  }

  return null;
};

const formatBarWeightRemnantAvailability = (product: Product): string => {
  const weightInfo = `${formatNumber(Math.round(product.quantity!.value))} ${
    product.quantity!.unit
  }`;
  return `1 piece (≈${weightInfo})`;
};

const formatBarAvailability = (
  product: Product,
  dimensionUnit: string,
): string | null => {
  if (isBarWeightRemnant(product)) {
    return formatBarWeightRemnantAvailability(product);
  }
  const available = getBarAvailabilityInUnit(product, dimensionUnit);
  if (available === null) return null;
  // Never over-promise: show only the whole units the buyer can actually order.
  return `${formatNumber(floorOrderableQuantity(available))} ${dimensionUnit}`;
};

const GENERIC_PIECE_UNIT_TOKENS = [
  "unit",
  "units",
  "pc",
  "pcs",
  "piece",
  "pieces",
];

const formatUnitAvailability = (product: Product): string => {
  // assume isUnitUnit holds true
  let unit = product.quantity!.unit.toLocaleLowerCase();

  // Generic "unit"/"pcs"/etc. is shown to buyers as "piece(s)" rather than the
  // shape word, so a bar sold individually reads "10 pieces" (not "10 bars").
  if (GENERIC_PIECE_UNIT_TOKENS.includes(unit)) {
    unit = "piece";
  } else {
    unit = unit.replace(/s$/, ""); // remove plural 's' if present
  }

  if (product.quantity!.value > 1) {
    unit = `${unit}s`; // pluralize if quantity is more than 1
  }
  return `${formatNumber(product.quantity!.value)} ${unit}`;
};

const formatSheetAvailability = (product: Product): string | null => {
  // For a sheet or plate, we want to format the availability based on the unit count
  if (!product.quantity || !product.quantity.value) return null;
  if (isUnitUnit(product.quantity.unit)) {
    return formatUnitAvailability(product);
  }

  if (isWeightUnit(product.quantity.unit) && product.material?.density) {
    // Stock counted by weight (e.g. a leftover plate stocked as "663 lb"). Convert
    // to whole pieces via density + geometry. A remnant below a nominal piece is
    // still one physical piece, so we show at least "1" and surface the exact
    // stock weight for reference (the buyer buys the piece, priced by its weight).
    const density = product.material.density; // in kg/m^3
    const volume = calculateMaterialVolume(product); // in mm^3
    if (volume) {
      const weightInKg = normalizeWeightToKg(product.quantity);
      const unitWeightInKg = (volume * density) / 1e9; // convert mm^3 to m^3
      if (unitWeightInKg > 0) {
        const pieces = Math.max(
          1,
          floorOrderableQuantity(weightInKg / unitWeightInKg),
        );
        const word = product.shape?.toLowerCase().includes("plate")
          ? "plate"
          : "sheet";
        const label = pieces === 1 ? word : `${word}s`;
        const weightInfo = `${formatNumber(
          Math.round(product.quantity.value),
        )} ${product.quantity.unit}`;
        return `${formatNumber(pieces)} ${label} (≈${weightInfo})`;
      }
    }
  }

  // If we reach here, we don't have enough information to format the availability
  return null;
};

export const formatAvailability = (
  product: Product,
  dimensionUnit: string,
  options: {
    short?: boolean; // if true, return just the availability string without "available"
  } = {},
): string | null => {
  if (!product.quantity || !product.quantity.value) return null;

  const { value, unit } = product.quantity;

  if (value <= 0) return "Out of stock";

  // Honor a product's configured display unit. If it prefers a piece/unit
  // display and the stock is counted in pieces, show the piece count directly
  // (e.g. "10 pieces") instead of converting a bar's pieces into total length.
  const preferredUnit = canonicalizePriceUnit(product.displayUnits?.[0]);
  if (preferredUnit && isUnitUnit(preferredUnit) && isUnitUnit(unit)) {
    const pieces = formatUnitAvailability(product);
    return options.short ? pieces : `${pieces} available`;
  }

  // always show availability in larger units e.g. m instead of mm, ft instead of in
  if (!dimensionUnit) dimensionUnit = "ft"; // default to ft if no dimension unit is provided
  if (preferredUnit && isLengthUnit(preferredUnit))
    dimensionUnit = preferredUnit;
  if (dimensionUnit === "mm") dimensionUnit = "m";
  if (dimensionUnit === "in") dimensionUnit = "ft";

  const shape = product.shape || "";

  let availabilityString: string | null = null;

  if (shapeIsBarTube(shape)) {
    availabilityString = formatBarAvailability(product, dimensionUnit);
  } else if (shapeIsSheet(shape)) {
    availabilityString = formatSheetAvailability(product);
  } else if (isUnitUnit(unit)) {
    availabilityString = formatUnitAvailability(product);
  }

  if (!availabilityString) {
    // Fallback for other shapes or if the conversions failed, just return the quantity and unit
    availabilityString = `${formatNumber(value)} ${unit}`;
  }

  if (!availabilityString) return null;
  if (options.short) return availabilityString;
  return `${availabilityString} available`;
};
